/* eslint-disable @nx/enforce-module-boundaries */
import { useEffect, useState, useRef } from 'react';
import { Typography, Button, Box, Menu, MenuItem, Modal } from '@mui/material';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import arrow from "packages/novous_web/src/assets/icons/Arrow.svg";
import calendar from "packages/novous_web/src/assets/icons/calendar.svg";
import DateRangeFilter from "../../../../common/components/DateRange";
import dayjs from "dayjs";
import { ArrowBack } from '@mui/icons-material';
import VehicleDashboardCard from './VehicleDashboardCard';
import DayWiseKmRunning from '../../Home/graphs/DayWiseKmRunning';
import manageExt from 'packages/novous_web/src/assets/icons/CustomBlue.svg';
import DistanceWiseUtilizationGraph from '../../Home/graphs/DistanceWiseUtilizationGraph';
import TimeWiseUtilizationGraph from '../../Home/graphs/TimeWiseUtilizationGraph';
import AverageSpeedGraph from '../../Home/graphs/AverageSpeedGraph';
import SafetyScoreGraph from '../../Home/graphs/SafetyScoreGraph';
import VehicleDashboardWarningGraph from '../../Home/graphs/VehicleDashboardWarningGraph';
import FuelGraph from '../../Home/graphs/FuelGraph';
import { Card, CardContent, Grid } from '@mui/material';
type FuelDataPoint = {
  timestamp: string; // ISO format (e.g. "2025-07-24T14:30:00Z")
  fuelLevel: number | null; // can be null for missing points
};

// Import the VehicleManageSectionLayout modal component
import VehicleManageSectionLayout from '../../../../layouts/VehicleManageSectionLayout';
import { set } from 'react-datepicker/dist/date_utils';

interface VehicleInfo {
  activeTime: string;
blurStatus: boolean;
cameraBlockStatus: number;
cameraMalFunctionStatus: number;
chassisNo: string;
companyCode: string;
country: string;
createdAt: string;
customerId: string;
dataSource: string;
deviceId: string;
deviceTimeZone: string;
deviceType: string;
dmsCameraStatus: number;
driverName: string;
dsmCameraId: string;
dsmCameraPosition: number;
editedAt: string
errorStatus: number;
fcwCameraStatus: number;
fovStatus: number;
fuelDeviceId: string;
gpsStatus: number;
groupName: string;
hasDevice: boolean;
ignitionStatus: number;
imuStatus: number;
inCabinCameraId: string;
inCabinCameraStatus: number;
lastAlarmDataTime: string;
lastErrorEndTime: string;
lastErrorOccuredTime: string;
lastHealthDataTime: string;
lastIgnitionOnTime: string;
lastLatitude: number;
lastLocationAddress: string;
lastLongitude: number;
lowVisibilityStatus: number;
maxLiveStreamViewers: number;
maxViewDurationMillis: number;
micStatus: number;
model: string;
modelYear: string;
onlineState: number;
parentId: string;
powerTamperingStatus: number;
rearCameraId: string;
rearCameraPosition: number;
rearCameraStatus: number;
sdCardStatus: number;
signalStrengthStatus: number;
sosStatus: number;
speakerStatus: number;
totalUsedMb: number;
updatedTime: string;
vehicleManufacturer: string;
vehicleName: string;
vehicleRegistrationNo: string;
vehicleType: string;
version: string;
}

interface ContinuousDrivingReport {
  date_time: string;
  vehicleId: string;
  warningSortKey: string;
  continuousDrivingEndTime: string;
  km: number;
}

type TilesObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};

const data: ContinuousDrivingReport[] = [];

// Update SectionsConfig to only include keys related to dashboard graphs
interface SectionsConfig {
  "distance-wise-fleet-utilization-graph": boolean;
  "time-wise-fleet-utilization-graph": boolean;
  "day-wise-km-running-graph": boolean;
  "average-speed-graph": boolean;
  "fleet-safety-score": boolean;
  "day-wise-warnings": boolean;
  [key: string]: boolean;
}


// Utility: fill missing timestamps (every 30s) with null fuelLevel
const fillMissingFuelData = (
  data: FuelDataPoint[],
  timeBegin: number,
  timeEnd: number
): FuelDataPoint[] => {
  if (data == null || data.length === 0) return [];

  const filled: FuelDataPoint[] = [];
  const sorted = [...data].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  let currentTime = timeBegin * 1000;
  const endTime = timeEnd * 1000;
  let dataIdx = 0;
  let lastValue: number | null = null;

  while (currentTime <= endTime) {
    // Move dataIdx forward to the latest point <= currentTime
    while (
      dataIdx < sorted.length &&
      new Date(sorted[dataIdx].timestamp).getTime() <= currentTime
    ) {
      if (sorted[dataIdx].fuelLevel !== null) {
        lastValue = sorted[dataIdx].fuelLevel;
      }
      dataIdx++;
    }

    filled.push({
      timestamp: new Date(currentTime).toISOString(),
      fuelLevel: lastValue, // forward fill last known value
    });

    currentTime += 30_000;
  }

  return filled;
};


// Utility: calculate metrics
const calculateFuelMetrics = (data: FuelDataPoint[]) => {
  let refueled = 0;
  let drained = 0;
  let startFuel: number | null = null;
  let endFuel: number | null = null;

  let i = 0;
  while (i < data.length - 1) {
    const curr = data[i];
    const next = data[i + 1];
    if (curr.fuelLevel == null || next.fuelLevel == null) {
      i++;
      continue;
    }

    if (startFuel === null) startFuel = curr.fuelLevel;

    const diff = next.fuelLevel - curr.fuelLevel;

    // Refuel: continuous increase > 20L
    if (diff > 10) {
      refueled += diff;
    }

    // Drain: rapid drop > 1L in 30s, grouped > 20L
    if (diff < -1) {
      let dropSum = -diff;
      let j = i + 1;
      while (j < data.length - 1) {
        const d1 = data[j];
        const d2 = data[j + 1];
        if (d1.fuelLevel == null || d2.fuelLevel == null) break;
        const drop = d2.fuelLevel - d1.fuelLevel;
        if (drop < -1) {
          dropSum += -drop;
          j++;
        } else break;
      }
      if (dropSum > 10) drained += dropSum;
      i = j;
      continue;
    }

    i++;
  }

  for (let k = data.length - 1; k >= 0; k--) {
    if (data[k].fuelLevel != null) {
      endFuel = data[k].fuelLevel;
      break;
    }
  }

  let consumed = 0;
  if (startFuel !== null && endFuel !== null) {
    consumed = startFuel + refueled - endFuel - drained;
  }

  return {
    refueled: Math.round(refueled),
    drained: Math.round(drained),
    consumed: Math.round(consumed),
  };
};



export default function VehicleDashboard() {
  // Axios hook for fetching table graph data
  const {
    data: tableGraphData,
    error: tableGraphError,
    loading: tableGraphLoading,
    fetchData: fetchTableGraphData,
  } = useAxios(API_ENDPOINTS.dashboard.VEHICLE_TABLE_GRAPH, "POST");
  const [fuelData, setFuelData] = useState<FuelDataPoint[]>([]);


  const {
    data: vehicleByTypeData,
    error: vehicleByTypeError,
    loading: vehicleByTypeLoading,
    fetchData: fetchvehicleByTypeData,
  } = useAxios<VehicleInfo[]>(API_ENDPOINTS.dashboard.TOTAL_VEHICLES, "POST");

  useEffect(() => {
    if (tableGraphData) {
      console.log("Fetched Table Graph Data:", tableGraphData);
    }
  }, [tableGraphData]);

  // Manage dashboard sections configuration
  // Here we include only the sections that appear in the dashboard view.
  const [sectionsConfig, setSectionsConfig] = useState<SectionsConfig>({
    "distance-wise-fleet-utilization-graph": true,
    "time-wise-fleet-utilization-graph": true,
    "day-wise-km-running-graph": true,
    "average-speed-graph": true,
    "fleet-safety-score": true,
    "day-wise-warnings": true,
  });

  const [tilesObj, setTilesObj] = useState<TilesObjType>({
    Stat1: { name: "Total KM Driven", status: true },
    Stat2: { name: "Average KM Running", status: true },
    Stat3: { name: "Average Idling Time", status: true },
    Stat4: { name: "Average Driving Time", status: true },
    Stat5: { name: "Occupancy Count", status: true }, // Assuming occupancy count is part of the data
  });

  // State to control visibility of the Manage Sections modal
  const [openManageModal, setOpenManageModal] = useState(false);

  // Auth, routing, and location hooks
  const { user } = useAuth();
  const location = useLocation();
  const state = location.state;
  const navigate = useNavigate();
  console.log("User object:", user);
  // Date range values and handlers
  const dateRangeArr = [
    "Today",
    "Yesterday",
    "7 Days",
    "15 Days",
    "30 Days",
    "Custom Range",
  ];
  const [selectedDuration, setSelectedDuration] = useState(dateRangeArr[0]);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dStartDate, setdStartDate] = useState("");
  const [dEndDate, setdEndDate] = useState("");
  const [fuelMetrics, setFuelMetrics] = useState({
    refueled: 0,
    drained: 0,
    consumed: 0,
  });



  const getDateRange = (range: string) => {
    const today = dayjs();
    let startDate, endDate, AstartDate, AendDate;

    switch (range) {
      case "Today":
        startDate = today.subtract(5, "day").startOf('day').format("YYYY-MM-DD HH:mm:ss");
        AstartDate = today.format("YYYY-MM-DD");
        AendDate = today.format("YYYY-MM-DD");
        endDate = today.format("YYYY-MM-DD HH:mm:ss");
        break;

      case "Yesterday":
        const yesterday = today.subtract(1, "day");
        startDate = yesterday.subtract(5, "day").startOf('day').format("YYYY-MM-DD HH:mm:ss");
        AstartDate = yesterday.format("YYYY-MM-DD");
        AendDate = yesterday.format("YYYY-MM-DD");
        endDate = yesterday.endOf('day').format("YYYY-MM-DD HH:mm:ss");
        break;

      case "7 Days":
        startDate = today.subtract(6, "day").startOf('day').format("YYYY-MM-DD HH:mm:ss");
        AstartDate = today.subtract(6, "day").startOf('day').format("YYYY-MM-DD");
        AendDate = today.format("YYYY-MM-DD");
        endDate = today.format("YYYY-MM-DD HH:mm:ss");
        break;

      case "15 Days":
        startDate = today.subtract(14, "day").startOf('day').format("YYYY-MM-DD HH:mm:ss");
        AstartDate = today.subtract(14, "day").startOf('day').format("YYYY-MM-DD");
        AendDate = today.format("YYYY-MM-DD");
        endDate = today.format("YYYY-MM-DD HH:mm:ss");
        break;

      case "30 Days":
        startDate = today.subtract(29, "day").startOf('day').format("YYYY-MM-DD HH:mm:ss");
        AstartDate = today.subtract(29, "day").startOf('day').format("YYYY-MM-DD");
        AendDate = today.format("YYYY-MM-DD");
        endDate = today.format("YYYY-MM-DD HH:mm:ss");
        break;

      default:
        startDate = "";
        endDate = "";
        AstartDate = "";
        AendDate = "";
    }

    return { startDate, endDate, AstartDate, AendDate};
  };


  let { startDate: startDateC, endDate: endDateC, AstartDate : aStartDate, AendDate:aEndDate } = getDateRange(selectedDuration);

  useEffect(() => {
    if (startDateC !== "" && endDateC !== "") {
    }
  }, [startDateC, endDateC]);

  if (startDate !== "" && endDate !== "" ) {
    startDateC = startDate;
    endDateC = endDate;
    aStartDate = dStartDate;
    aEndDate = dEndDate;
    
  } else if (startDate === "" && endDate === "" ) {
    ({ startDate: startDateC, endDate: endDateC,  AstartDate : aStartDate, AendDate:aEndDate  } = getDateRange(selectedDuration));
  }

  useEffect(() => {
    if(selectedDuration === "Custom Range") {
      return;}
    const { startDate: newStartDate, endDate: newEndDate, AstartDate : newaStartDate, AendDate:newaEndDate } = getDateRange(selectedDuration);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setdStartDate(newaStartDate);
    setdEndDate(newaEndDate);
  }, [selectedDuration]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setCalendarAnchorEl(event.currentTarget);
  };



  const handleCalendarClose = () => {
    //setSelectedDuration("")
    setShowDatePicker(false);
    // setStartDate("");
    // setEndDate("");

    // setSelectedDuration(dateRangeArr[0]);
    setCalendarAnchorEl(null);
  };

  const handleClose = (value?: string) => {
    console.log("Selected Value:", value, selectedDuration);

    if (value) {
      // Only reset dates if changing FROM custom range TO another option
      // if (selectedDuration === "Custom Range" && value !== "Custom Range") {
      //   const { startDate: newStartDate, endDate: newEndDate, AstartDate : newaStartDate, AendDate: newaEndDate} = getDateRange(value);
      //   setStartDate(newStartDate);
      //   setEndDate(newEndDate);
      //   setdStartDate(newaStartDate);
      //   setdEndDate(newaEndDate);
       
      // }
      if( value !== "Custom Range") {
      setSelectedDuration(value);}
      setShowDatePicker(value === "Custom Range");

      // If selecting Custom Range again, don't reset the dates - keep existing values
      if (value === "Custom Range") {
        setCalendarAnchorEl(anchorEl); // Keep the anchor for the date picker
      }
    }

    setAnchorEl(null);
  };

  // Ref to prevent duplicate API calls for TABLE_GRAPH
  const lastCalledRef = useRef({
    startDate: "",
    endDate: "",
    fleetId: null,
    vehicleId: undefined,
  });

  // Fetch TABLE_GRAPH data when the date range, fleetId, or vehicle changes.
  useEffect(() => {
    if (startDateC && endDateC && user?.customer_id) {
      if (
        lastCalledRef.current.startDate !== startDateC ||
        lastCalledRef.current.endDate !== endDateC ||
        lastCalledRef.current.fleetId !== user?.customer_id ||
        lastCalledRef.current.vehicleId !== state?.vehicleRegistrationNo
      ) {
        fetchTableGraphData({
          startDate: startDateC.split(' ')[0],
          endDate: endDateC.split(' ')[0],
          fleetId: user?.customer_id,
          vehicleId: state?.vehicleRegistrationNo,
        });
        lastCalledRef.current = {
          startDate: startDateC.split(' ')[0],
          endDate: endDateC.split(' ')[0],
          fleetId: user?.customer_id,
          vehicleId: state?.vehicleRegistrationNo,
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateC, endDateC, user?.customer_id, state?.vehicleRegistrationNo]);

  function formatLastUpdateTime(rawTime: string | undefined): string {
    if (!rawTime) return '';
    // Normalize format: replace both `:` and `_` with `-`
    const normalized = rawTime.replace(/[_:]/g, '-');
    // Try parsing with known expected structure
    const parts = normalized.split('-');
    if (parts.length >= 6) {
      // Construct date string in YYYY-MM-DD HH:mm:ss format
      const formattedInput = `${parts[0]}-${parts[1]}-${parts[2]} ${parts[3]}:${parts[4]}:${parts[5]}`;
      return dayjs(formattedInput).format('DD MMM YYYY, HH:mm:ss');
    }
    return ''; // fallback
  }


  const fetchJwtToken = async () => {
    try {
      const response = await fetch('https://online.omnicomm.in/auth/login?jwt=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ login: 'demo_in', password: 'lFg3v62HqE1' }),
      });
      const data = await response.json();
      return data.jwt;
    } catch (error) {
      console.error('JWT fetch error:', error);
      return null;
    }
  };

  // const fetchVehicleInfo = async (fleetId: string, vehicleId: string, jwtToken: string) => {
  //   try {
  //     const response = await fetch('https://backend.novusomnifleet.com/dashboard/get-all-vehicle-by-type', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${jwtToken}`,
  //       },
  //       body: JSON.stringify({
  //         fleetId,
  //         vehicleId,
  //       }),
  //     });
  //     const data = await response.json();

  //     return data; // This is the array of vehicle info
  //   } catch (error) {
  //     console.error('Vehicle info fetch error:', error);
  //     return [];
  //   }
  // };

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        fleetId: user?.customer_id,
        vehicleId: state?.vehicleRegistrationNo,
      };

      try {
        await fetchvehicleByTypeData(payload);
      } catch (error) {
        console.error('Vehicle info fetch error:', error);
      }
    };

    fetchData();
  }, []);


  console.log(vehicleByTypeData, "vehicleByTypeData")

  const fetchFuelData = async (jwtToken: string, VehicleId: string, timeBegin: number, timeEnd: number) => {
    try {
      const response = await fetch(`https://online.omnicomm.in/ls/api/v1/reports/fuellevel?vehicleID=${VehicleId}&timeBegin=${timeBegin}&timeEnd=${timeEnd}`, {
        method: 'GET',
        headers: { Authorization: `JWT ${jwtToken}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      return data.tankData[0]?.data || [];
    } catch (error) {
      console.error('Fuel data fetch error:', error);
      return [];
    }
  };

  useEffect(() => {
    const getFuelGraphData = async () => {
      // Check if we have vehicle data
      if (!vehicleByTypeData) {
        console.log("No vehicle data available");
        return;
      }

      const vehicleInfo = vehicleByTypeData[0]; // Get first vehicle (assuming it's the correct one)
      console.log("Vehicle Info:", vehicleInfo);

      if (!vehicleInfo?.fuelDeviceId) {
        console.log("No fuel device found for this vehicle number:", state.vehicleRegistrationNo);
        return; // Exit if no fuel device
      }

      console.log("Fuel device found for this vehicle:", vehicleInfo.fuelDeviceId);

      const jwtToken = await fetchJwtToken();
      console.log("JWT Token:", jwtToken);
      if (!jwtToken) return;

      const timeBegin = Math.floor(dayjs(startDateC).valueOf() / 1000);
      const timeEnd = Math.floor(dayjs(endDateC).valueOf() / 1000);

      try {
        const rawFuelData = await fetchFuelData(jwtToken, vehicleInfo.fuelDeviceId, timeBegin, timeEnd);
        const mappedFuelData: FuelDataPoint[] = rawFuelData.map((d: { eD: number; rV?: number | null; aV?: number | null }) => ({
          timestamp: new Date(d.eD * 1000).toISOString(),
          fuelLevel: d.aV ?? null,
        }));

        const filledData: FuelDataPoint[] = fillMissingFuelData(mappedFuelData, timeBegin, timeEnd);
        const metrics = calculateFuelMetrics(filledData);

        setFuelData(filledData);
        setFuelMetrics(metrics);
      } catch (err) {
        console.error('Fuel data processing failed:', err);
      }
    };

    getFuelGraphData();
  }, [user?.customer_id, startDateC, endDateC, vehicleByTypeData]);

  //   useEffect(() => {
  //   const getFuelGraphData = async () => {
  //       const jwtToken = await fetchJwtToken();
  //       if (!jwtToken) return;
  //       const vehicleInfoArr = await fetchVehicleInfo(user.customer_id, state.vehicleRegistrationNo, sessionStorage.getItem("authToken") || jwtToken);
  //       const vehicleInfo = vehicleInfoArr[0];
  //       console.log("Vehicle Info:", vehicleInfo);
  //       console.log("state ", state);
  //       if(!vehicleInfo?.fuelDeviceId){
  //         console.log("No fuel device found for this vehicle number: ", state.vehicleRegistrationNo);
  //       }
  //       if(vehicleInfo?.fuelDeviceId) {
  //         console.log("fuel device found for this vehicle:", vehicleInfo.fuelDeviceId);
  //       }

  //       const timeBegin = Math.floor(dayjs(startDateC).valueOf() / 1000);
  //       const timeEnd = Math.floor(dayjs(endDateC).valueOf() / 1000);

  //       try {
  //         const rawFuelData = await fetchFuelData(jwtToken, vehicleInfo.fuelDeviceId,timeBegin, timeEnd);
  //         // Right after fetching rawFuelData:
  //         const mappedFuelData: FuelDataPoint[] = rawFuelData.map((d: { eD: number; rV?: number | null; aV?: number | null }) => ({
  //         timestamp: new Date(d.eD * 1000).toISOString(),
  //         fuelLevel: d.aV ?? null, // or d.aV ?? null if that's more accurate
  //       }));

  // // Now pass mappedFuelData to fillMissingFuelData:
  //         const filledData: FuelDataPoint[] = fillMissingFuelData(mappedFuelData, timeBegin, timeEnd);
  //         // Fill missing timestamps with nulls every 30s
  //         //const filledData: FuelDataPoint[] = fillMissingFuelData(rawFuelData, timeBegin, timeEnd);

  //         // Compute metrics from filled data
  //         const metrics = calculateFuelMetrics(filledData);

  //         // Update state
  //         setFuelData(filledData);
  //         setFuelMetrics(metrics);
  //       } catch (err) {
  //         console.error('Fuel data processing failed:', err);
  //       }

  //   };

  //   getFuelGraphData();
  // }, [user?.customer_id, startDateC, endDateC]);

  const graphData = fuelData.map(d => ({
    eD: new Date(d.timestamp).getTime() / 1000,
    rV: d.fuelLevel ?? 0,
    aV: d.fuelLevel ?? 0,
  }));
  const chartData = fuelData.filter((_, idx) => idx % 60 === 0); // show every 10th point
  return (
    <>
      <div className="flex flex-col gap-4 w-full">
        {/* Back Button */}
        <div className="w-full pt-8">
          <button className="text-blue-600 font-medium flex items-center" onClick={() => navigate(-1)}>
            <ArrowBack className="mr-1" /> Back
          </button>
        </div>
        <div className="w-full flex justify-between gap-4 xs:flex-col items-start">
          <div className="max-w-max">
            <div className="dashboard-header-left">
              <div className="flex flex-col items-start">
                <Typography variant="h4" fontSize="24px" fontWeight={600} lineHeight="36px">
                  {state?.vehicleRegistrationNo}
                </Typography>
                <Typography variant="subtitle1" fontSize={16} fontWeight={400} lineHeight="24px">
                  Last Data Received : {formatLastUpdateTime(state?.lastUpdateTime)}
                </Typography>
              </div>
            </div>
          </div>
          <div className="flex gap-4 flex-wrap">
            {selectedDuration === "Custom Range" && (
              <div className="flex items-center">
                <Box
                  sx={(theme) => ({
                    backgroundColor: theme.palette.selected,
                    borderRadius: "8px",
                    padding: theme.spacing(1),
                  })}
                >
                  <Typography
                    variant="subtitle2"
                    className="truncate"
                    sx={{ fontWeight: 700, marginRight: 1 }}
                  >
                    <span style={{ color: "#235AF9" }}>
                      {startDateC} - {endDateC}
                    </span>
                  </Typography>
                </Box>
              </div>
            )}
            <div className="relative">
              <Button
                onClick={handleClick}
                variant="outlined"
                sx={{
                  height: { xs: "28px", sm: "30px", md: "48px" },
                  px: { xs: 1, sm: 2 },
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  borderColor: "#5547dd",
                  backgroundColor: "white",
                  color: "#5547dd",
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img src={calendar} alt="Calendar" width={18} height={18} />
                  <Box sx={{ display: "block", maxWidth: "100%" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedDuration}
                    </Typography>
                  </Box>
                  <img src={arrow} alt="Arrow" width={18} height={18} />
                </Box>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => handleClose()}
                PaperProps={{
                  sx: {
                    borderRadius: "8px",
                    top: "10px",
                    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                {dateRangeArr.map((label) => (
                  <MenuItem
                    key={label}
                    onClick={() => handleClose(label)}
                    sx={(theme) => ({
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      backgroundColor: selectedDuration === label ? theme.palette.selected : 'transparent',
                    })}
                  >
                    <Typography variant="button" textTransform={"none"}>
                      {label}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
              <Menu
                anchorEl={calendarAnchorEl}
                open={showDatePicker}
                onClose={handleCalendarClose}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "8px",
                      top: "10px",
                      padding: "0px",
                      boxShadow: "0 6px 10px rgba(0, 0, 0, 0.1)",
                    },
                  },
                }}
              >
                <DateRangeFilter
                  onCancel={handleCalendarClose}
                  onApply={(from, to) => {
                    setStartDate(from);
                    setEndDate(to);
                    setdStartDate(from);
                    setdEndDate(to);
                    setSelectedDuration("Custom Range");
                    setShowDatePicker(false);
                    setCalendarAnchorEl(null);
                  }}
                  initialFrom={dStartDate}
                  initialTo={dEndDate}
                />
              </Menu>
            </div>
            <CustomIconButton
              icon={
                <img
                  src={manageExt}
                  alt="Custom"
                  style={{
                    filter: "brightness(0) invert(1)",
                    width: 16,
                    height: 16,
                  }}
                />
              }
              text="Manage Sections"
              variant="contained"
              outline={false}
              onClick={() => setOpenManageModal(true)}
            />
          </div>
        </div>
      </div>
      {dStartDate && dEndDate && (<VehicleDashboardCard
        startDate={dStartDate}
        endDate={dEndDate}
        fleetId={user?.customer_id}
        tilesObj={tilesObj}
        vehicleId={state?.vehicleRegistrationNo}
      />)}
      <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-4">
        <div className={`flex-1 ${!sectionsConfig["distance-wise-fleet-utilization-graph"] ? "hidden" : ""}`}>
          <DistanceWiseUtilizationGraph
            startDate={startDateC}
            endDate={endDateC}
            fleetId={user?.customer_id}
            tableGraphData={tableGraphData}
          />
        </div>
        <div className={`flex-1 ${!sectionsConfig["time-wise-fleet-utilization-graph"] ? "hidden" : ""}`}>
          <TimeWiseUtilizationGraph
            startDate={startDateC}
            endDate={endDateC}
            fleetId={user?.customer_id}
            tableGraphData={tableGraphData}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-4">
        <div className={`flex-1 ${!sectionsConfig["day-wise-km-running-graph"] ? "hidden" : ""}`}>
          <DayWiseKmRunning
            startDate={startDateC}
            endDate={endDateC}
            fleetId={user?.customer_id}
            tableGraphData={tableGraphData}
          />
        </div>
        <div className={`flex-1 ${!sectionsConfig["average-speed-graph"] ? "hidden" : ""}`}>
          <AverageSpeedGraph
            startDate={startDateC}
            endDate={endDateC}
            fleetId={user?.customer_id}
            tableGraphData={tableGraphData}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-4">
        <div className={`flex-1 ${!sectionsConfig["fleet-safety-score"] ? "hidden" : ""}`}>
          <SafetyScoreGraph tableGraphData={tableGraphData} removeCustomFontWeight removeCustomColor />
        </div>
        <div className={`flex-1 ${!sectionsConfig["day-wise-warnings"] ? "hidden" : ""}`}>
          <VehicleDashboardWarningGraph tableGraphData={tableGraphData} />
        </div>
      </div>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Total Fuel Refueled</Typography>
              <Typography variant="h6" color="success.main">{fuelMetrics.refueled.toFixed(2)} L</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Total Fuel Drained</Typography>
              <Typography variant="h6" color="error.main">{fuelMetrics.drained.toFixed(2)} L</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Total Fuel Consumed</Typography>
              <Typography variant="h6" color="primary.main">{fuelMetrics.consumed.toFixed(2)} L</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {vehicleByTypeData && vehicleByTypeData[0]?.fuelDeviceId &&(<div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-4">

        <div className="flex-1">

          <FuelGraph
            fuelData={chartData.map((d: FuelDataPoint) => ({
              eD: new Date(d.timestamp).getTime() / 1000,
              rV: d.fuelLevel ?? 0,
              aV: d.fuelLevel ?? 0,
            }))}
          />
        </div>
      </div>)}


      {/* Updated Manage Sections Modal using Modal & Box */}
      <Modal
        open={openManageModal}
        onClose={() => setOpenManageModal(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "8px",
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
            p: 2,
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <VehicleManageSectionLayout
            onClose={() => setOpenManageModal(false)}
            sectionsConfig={sectionsConfig}
            setSectionsConfig={setSectionsConfig}
            tilesObj={tilesObj}
            setTilesObj={setTilesObj}
            onToggleSection={(sectionId: keyof SectionsConfig) =>
              console.log(`Toggled section: ${sectionId}`)
            }
          />
        </Box>
      </Modal>
    </>
  );
}